import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type Board, useUpdateBoard } from '@/entities/board';
import { InlineTitleEditor } from '@/shared/ui';

import styles from './BoardManage.module.css';

const NAME_MAX = 200;

/**
 * Board title in the board header: click it to rename in place (Enter saves, Escape cancels,
 * blur saves a changed non-empty name). The settings dialog stays as the other way to rename.
 */
export function BoardTitle({ board, canEdit }: { board: Board; canEdit: boolean }) {
  const { t } = useTranslation();
  const update = useUpdateBoard(board);
  const [editing, setEditing] = useState(false);

  if (editing) {
    return (
      <div className={styles.titleEdit}>
        <InlineTitleEditor
          name={board.name}
          label={t('boards.nameInline')}
          className={styles.titleInput}
          maxLength={NAME_MAX}
          onSave={(name) => update.mutateAsync({ name })}
          onClose={() => {
            setEditing(false);
          }}
        />
      </div>
    );
  }
  if (!canEdit) return <h1 className={styles.title}>{board.name}</h1>;
  return (
    <h1 className={styles.title}>
      {/* A span, not a <button>: a heading's text should wrap like any title. */}
      <span
        role="button"
        tabIndex={0}
        className={styles.titleButton}
        title={t('boards.renameHint')}
        onClick={() => {
          setEditing(true);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setEditing(true);
          }
        }}
      >
        {board.name}
      </span>
    </h1>
  );
}
